import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  IndianRupee, Wallet, TrendingUp, TrendingDown, Users, CreditCard, 
  Banknote, Smartphone, Building2, FileText, Download, Plus, 
  Check, Clock, AlertCircle, Search, Filter, RefreshCw,
  ChevronRight, Eye, Receipt, Calculator, PiggyBank
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FinanceOverview = ({ theme }) => {
  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';
  
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [gstData, setGstData] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [refunds, setRefunds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  // Modals
  const [showCashModal, setShowCashModal] = useState(false);
  const [showUPIModal, setShowUPIModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentFinance, setStudentFinance] = useState(null);
  
  // Filters
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Form states
  const [cashForm, setCashForm] = useState({
    student_id: '', student_name: '', course_id: '', course_name: '',
    amount: '', payment_purpose: 'admission', received_by: '', notes: ''
  });
  const [upiForm, setUpiForm] = useState({
    student_id: '', student_name: '', course_id: '', course_name: '',
    amount: '', upi_app: 'gpay', utr_number: '', payment_date: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    loadFinanceData();
  }, []);

  const loadFinanceData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [overviewRes, transactionsRes, gstRes, pipelineRes, refundsRes] = await Promise.all([
        axios.get(`${API}/api/finances/overview`, { headers, withCredentials: true }),
        axios.get(`${API}/api/finances/transactions`, { headers, withCredentials: true }),
        axios.get(`${API}/api/finances/gst`, { headers, withCredentials: true }),
        axios.get(`${API}/api/finances/pipeline`, { headers, withCredentials: true }),
        axios.get(`${API}/api/finances/refunds`, { headers, withCredentials: true })
      ]);
      
      setOverview(overviewRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setGstData(gstRes.data);
      setPipeline(pipelineRes.data);
      setRefunds(refundsRes.data);
    } catch (err) {
      console.error('Error loading finance data:', err);
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCashPayment = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/api/finances/cash`, {
        ...cashForm,
        amount: parseFloat(cashForm.amount)
      }, { headers, withCredentials: true });
      
      toast.success('Cash payment recorded successfully');
      setShowCashModal(false);
      setCashForm({
        student_id: '', student_name: '', course_id: '', course_name: '',
        amount: '', payment_purpose: 'admission', received_by: '', notes: ''
      });
      loadFinanceData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleAddUPIPayment = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/api/finances/upi`, {
        ...upiForm,
        amount: parseFloat(upiForm.amount)
      }, { headers, withCredentials: true });
      
      toast.success('UPI payment recorded successfully');
      setShowUPIModal(false);
      setUpiForm({
        student_id: '', student_name: '', course_id: '', course_name: '',
        amount: '', upi_app: 'gpay', utr_number: '', payment_date: ''
      });
      loadFinanceData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const loadStudentFinance = async (studentId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/api/finances/students/${studentId}`, { headers, withCredentials: true });
      setStudentFinance(res.data);
      setShowStudentModal(true);
    } catch (err) {
      toast.error('Failed to load student finance data');
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount?.toLocaleString() || 0}`;
  };

  const filteredTransactions = transactions.filter(t => {
    if (transactionFilter === 'all') return true;
    return t.type === transactionFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Financial Overview</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadFinanceData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Top KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Total Invoiced</p>
            <p className="text-lg font-bold text-emerald-600">{formatCurrency(overview?.kpi?.total_fees_invoiced)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Collected</p>
            <p className="text-lg font-bold text-blue-600">{formatCurrency(overview?.kpi?.total_fees_collected)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="text-lg font-bold text-orange-600">{formatCurrency(overview?.kpi?.pending_fees)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">GST Collected</p>
            <p className="text-lg font-bold text-purple-600">{formatCurrency(overview?.kpi?.gst_collected)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">GST Payable</p>
            <p className="text-lg font-bold text-red-600">{formatCurrency(overview?.kpi?.gst_payable)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Net Revenue</p>
            <p className="text-lg font-bold text-teal-600">{formatCurrency(overview?.kpi?.net_revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Cash In Hand</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(overview?.kpi?.cash_in_hand)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-500/10 to-blue-500/10 border-indigo-500/20">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">UPI/Online</p>
            <p className="text-lg font-bold text-indigo-600">{formatCurrency(overview?.kpi?.upi_online_balance)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Sub Navigation Tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="transactions" className="text-xs">Transactions</TabsTrigger>
          <TabsTrigger value="students" className="text-xs">Students</TabsTrigger>
          <TabsTrigger value="gst" className="text-xs">GST</TabsTrigger>
          <TabsTrigger value="pipeline" className="text-xs">Pipeline</TabsTrigger>
          <TabsTrigger value="refunds" className="text-xs">Refunds</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Payment Breakdown */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  Online Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatCurrency(overview?.breakdown?.online_payments)}</p>
                <p className="text-xs text-muted-foreground">{overview?.counts?.online_transactions} transactions</p>
              </CardContent>
            </Card>
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-purple-500" />
                  UPI Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatCurrency(overview?.breakdown?.upi_payments)}</p>
                <p className="text-xs text-muted-foreground">{overview?.counts?.upi_transactions} transactions</p>
                <Button size="sm" className="mt-2" onClick={() => setShowUPIModal(true)}>
                  <Plus className="w-3 h-3 mr-1" /> Add UPI
                </Button>
              </CardContent>
            </Card>
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-green-500" />
                  Cash Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatCurrency(overview?.breakdown?.cash_payments)}</p>
                <p className="text-xs text-muted-foreground">{overview?.counts?.cash_transactions} transactions</p>
                <Button size="sm" className="mt-2" onClick={() => setShowCashModal(true)}>
                  <Plus className="w-3 h-3 mr-1" /> Add Cash
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="text-sm">Collection Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Collection Rate</span>
                  <Badge variant={overview?.kpi?.total_fees_collected >= overview?.kpi?.total_fees_invoiced * 0.8 ? 'default' : 'destructive'}>
                    {overview?.kpi?.total_fees_invoiced > 0 
                      ? Math.round((overview?.kpi?.total_fees_collected / overview?.kpi?.total_fees_invoiced) * 100)
                      : 0}%
                  </Badge>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-emerald-500 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${overview?.kpi?.total_fees_invoiced > 0 
                        ? Math.min(100, (overview?.kpi?.total_fees_collected / overview?.kpi?.total_fees_invoiced) * 100)
                        : 0}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span>Collected: {formatCurrency(overview?.kpi?.total_fees_collected)}</span>
                  <span>Target: {formatCurrency(overview?.kpi?.total_fees_invoiced)}</span>
                </div>
              </CardContent>
            </Card>

            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCashModal(true)}>
                  <Banknote className="w-4 h-4 mr-2" /> Record Cash
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowUPIModal(true)}>
                  <Smartphone className="w-4 h-4 mr-2" /> Record UPI
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveSubTab('gst')}>
                  <Calculator className="w-4 h-4 mr-2" /> GST Report
                </Button>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" /> Export All
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4 mt-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex gap-2">
              {['all', 'online', 'upi', 'cash'].map(filter => (
                <Button 
                  key={filter}
                  variant={transactionFilter === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTransactionFilter(filter)}
                >
                  {filter === 'all' ? 'All' : filter.toUpperCase()}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={() => setShowCashModal(true)}>
                <Plus className="w-4 h-4 mr-1" /> Cash Entry
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowUPIModal(true)}>
                <Plus className="w-4 h-4 mr-1" /> UPI Entry
              </Button>
            </div>
          </div>

          <Card className={cardClass}>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{t.date?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-left"
                            onClick={() => loadStudentFinance(t.student_id)}
                          >
                            {t.student_name}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate">{t.course_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            t.type === 'online' ? 'bg-blue-500/10 text-blue-600' :
                            t.type === 'upi' ? 'bg-purple-500/10 text-purple-600' :
                            'bg-green-500/10 text-green-600'
                          }>
                            {t.type?.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">₹{t.gross_amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-muted-foreground">₹{t.gst_amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-right">₹{t.net_amount?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={t.status === 'paid' ? 'default' : 'secondary'}>
                            {t.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">
                          {t.transaction_id || t.invoice_number}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students" className="space-y-4 mt-6">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="text-sm">Student-wise Fee Tracking</CardTitle>
              <CardDescription>Click on a student to view detailed payment history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {transactions.reduce((acc, t) => {
                  if (!acc.find(s => s.student_id === t.student_id)) {
                    acc.push({ student_id: t.student_id, student_name: t.student_name });
                  }
                  return acc;
                }, []).map((student, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => loadStudentFinance(student.student_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {student.student_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium">{student.student_name}</p>
                        <p className="text-xs text-muted-foreground">{student.student_id}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GST Tab */}
        <TabsContent value="gst" className="space-y-4 mt-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10">
              <CardContent className="p-4 text-center">
                <Calculator className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold">{formatCurrency(gstData?.summary?.total_gst_collected)}</p>
                <p className="text-sm text-muted-foreground">GST Collected</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10">
              <CardContent className="p-4 text-center">
                <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-bold">{formatCurrency(gstData?.summary?.total_gst_paid)}</p>
                <p className="text-sm text-muted-foreground">GST Paid</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10">
              <CardContent className="p-4 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                <p className="text-2xl font-bold">{formatCurrency(gstData?.summary?.gst_pending)}</p>
                <p className="text-sm text-muted-foreground">GST Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10">
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-lg font-bold">{gstData?.summary?.due_date}</p>
                <p className="text-sm text-muted-foreground">Next Due Date</p>
              </CardContent>
            </Card>
          </div>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                GST Ledger (Monthly)
              </CardTitle>
              <CardDescription>Exportable for CA filing</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">GST Collected</TableHead>
                    <TableHead className="text-right">GST Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gstData?.ledger?.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{row.month}</TableCell>
                      <TableCell className="text-right">₹{row.collected?.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-green-600">₹{row.paid?.toLocaleString()}</TableCell>
                      <TableCell className={`text-right font-medium ${row.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{row.balance?.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end mt-4">
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" /> Export GST Report
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-4 mt-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className={cardClass}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{formatCurrency(pipeline?.totals?.total)}</p>
                <p className="text-sm text-muted-foreground">Total Pipeline</p>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{formatCurrency(pipeline?.totals?.high_probability)}</p>
                <p className="text-sm text-muted-foreground">High Probability</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-500/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(pipeline?.totals?.medium_probability)}</p>
                <p className="text-sm text-muted-foreground">Medium Probability</p>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{formatCurrency(pipeline?.totals?.low_probability)}</p>
                <p className="text-sm text-muted-foreground">Low Probability</p>
              </CardContent>
            </Card>
          </div>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>Expected Payments</CardTitle>
            </CardHeader>
            <CardContent>
              {pipeline?.pipeline?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Expected Date</TableHead>
                      <TableHead>Probability</TableHead>
                      <TableHead>Category</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pipeline.pipeline.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{p.student_name}</TableCell>
                        <TableCell>{p.course_name}</TableCell>
                        <TableCell className="text-right font-medium">₹{p.expected_amount?.toLocaleString()}</TableCell>
                        <TableCell>{p.expected_date}</TableCell>
                        <TableCell>
                          <Badge variant={
                            p.probability === 'high' ? 'default' :
                            p.probability === 'medium' ? 'secondary' : 'destructive'
                          }>
                            {p.probability}
                          </Badge>
                        </TableCell>
                        <TableCell className="capitalize">{p.category?.replace('_', ' ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">No pending payments in pipeline</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Refunds Tab */}
        <TabsContent value="refunds" className="space-y-4 mt-6">
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-red-500/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{formatCurrency(refunds?.totals?.total_refunded)}</p>
                <p className="text-sm text-muted-foreground">Total Refunded</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-500/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(refunds?.totals?.pending_refunds)}</p>
                <p className="text-sm text-muted-foreground">Pending Refunds</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-500/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(refunds?.totals?.adjustments)}</p>
                <p className="text-sm text-muted-foreground">Adjustments</p>
              </CardContent>
            </Card>
          </div>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>Refunds & Adjustments</CardTitle>
            </CardHeader>
            <CardContent>
              {refunds?.refunds?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Refund ID</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {refunds.refunds.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-sm">{r.refund_id}</TableCell>
                        <TableCell>{r.student_id}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">₹{r.refund_amount?.toLocaleString()}</TableCell>
                        <TableCell className="capitalize">{r.refund_type}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{r.reason}</TableCell>
                        <TableCell>
                          <Badge variant={r.status === 'completed' ? 'default' : 'secondary'}>
                            {r.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">No refunds recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cash Payment Modal */}
      <Dialog open={showCashModal} onOpenChange={setShowCashModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-500" />
              Record Cash Payment
            </DialogTitle>
            <DialogDescription>Add a manual cash payment entry</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Student ID</Label>
                <Input 
                  value={cashForm.student_id}
                  onChange={(e) => setCashForm({...cashForm, student_id: e.target.value})}
                  placeholder="user_xxx"
                />
              </div>
              <div>
                <Label>Student Name</Label>
                <Input 
                  value={cashForm.student_name}
                  onChange={(e) => setCashForm({...cashForm, student_name: e.target.value})}
                  placeholder="Enter name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Course ID</Label>
                <Input 
                  value={cashForm.course_id}
                  onChange={(e) => setCashForm({...cashForm, course_id: e.target.value})}
                  placeholder="VIA001"
                />
              </div>
              <div>
                <Label>Course Name</Label>
                <Input 
                  value={cashForm.course_name}
                  onChange={(e) => setCashForm({...cashForm, course_name: e.target.value})}
                  placeholder="Course name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹)</Label>
                <Input 
                  type="number"
                  value={cashForm.amount}
                  onChange={(e) => setCashForm({...cashForm, amount: e.target.value})}
                  placeholder="25000"
                />
              </div>
              <div>
                <Label>Purpose</Label>
                <Select value={cashForm.payment_purpose} onValueChange={(v) => setCashForm({...cashForm, payment_purpose: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admission">Admission</SelectItem>
                    <SelectItem value="installment">Installment</SelectItem>
                    <SelectItem value="late_fee">Late Fee</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Received By</Label>
              <Input 
                value={cashForm.received_by}
                onChange={(e) => setCashForm({...cashForm, received_by: e.target.value})}
                placeholder="Staff name"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={cashForm.notes}
                onChange={(e) => setCashForm({...cashForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCashModal(false)}>Cancel</Button>
            <Button onClick={handleAddCashPayment}>
              <Check className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPI Payment Modal */}
      <Dialog open={showUPIModal} onOpenChange={setShowUPIModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-purple-500" />
              Record UPI Payment
            </DialogTitle>
            <DialogDescription>Add a direct UPI payment entry</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Student ID</Label>
                <Input 
                  value={upiForm.student_id}
                  onChange={(e) => setUpiForm({...upiForm, student_id: e.target.value})}
                  placeholder="user_xxx"
                />
              </div>
              <div>
                <Label>Student Name</Label>
                <Input 
                  value={upiForm.student_name}
                  onChange={(e) => setUpiForm({...upiForm, student_name: e.target.value})}
                  placeholder="Enter name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Course ID</Label>
                <Input 
                  value={upiForm.course_id}
                  onChange={(e) => setUpiForm({...upiForm, course_id: e.target.value})}
                  placeholder="VIA001"
                />
              </div>
              <div>
                <Label>Course Name</Label>
                <Input 
                  value={upiForm.course_name}
                  onChange={(e) => setUpiForm({...upiForm, course_name: e.target.value})}
                  placeholder="Course name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹)</Label>
                <Input 
                  type="number"
                  value={upiForm.amount}
                  onChange={(e) => setUpiForm({...upiForm, amount: e.target.value})}
                  placeholder="25000"
                />
              </div>
              <div>
                <Label>UPI App</Label>
                <Select value={upiForm.upi_app} onValueChange={(v) => setUpiForm({...upiForm, upi_app: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpay">Google Pay</SelectItem>
                    <SelectItem value="phonepe">PhonePe</SelectItem>
                    <SelectItem value="paytm">Paytm</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>UTR Number</Label>
              <Input 
                value={upiForm.utr_number}
                onChange={(e) => setUpiForm({...upiForm, utr_number: e.target.value})}
                placeholder="Transaction reference"
              />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date"
                value={upiForm.payment_date}
                onChange={(e) => setUpiForm({...upiForm, payment_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUPIModal(false)}>Cancel</Button>
            <Button onClick={handleAddUPIPayment}>
              <Check className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Finance Modal */}
      <Dialog open={showStudentModal} onOpenChange={setShowStudentModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Student Finance Profile
            </DialogTitle>
          </DialogHeader>
          {studentFinance && (
            <div className="space-y-4">
              {/* Student Header */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                  {studentFinance.student?.name?.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{studentFinance.student?.name}</h3>
                  <p className="text-muted-foreground">{studentFinance.student?.email}</p>
                  <p className="text-sm text-muted-foreground">{studentFinance.student?.phone}</p>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">₹{studentFinance.summary?.total_course_fee?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Fee</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-500/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">₹{studentFinance.summary?.total_paid?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Paid</p>
                  </CardContent>
                </Card>
                <Card className={studentFinance.summary?.pending_amount > 0 ? 'bg-red-500/10' : 'bg-green-500/10'}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-2xl font-bold ${studentFinance.summary?.pending_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ₹{studentFinance.summary?.pending_amount?.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Pending</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Payment Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Mode</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">GST</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {studentFinance.payment_timeline?.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{p.date?.slice(0, 10)}</TableCell>
                          <TableCell>{p.mode}</TableCell>
                          <TableCell className="text-right">₹{p.gross?.toLocaleString()}</TableCell>
                          <TableCell className="text-right">₹{p.gst?.toLocaleString()}</TableCell>
                          <TableCell className="text-right">₹{p.net?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge variant={p.status === 'Paid' ? 'default' : 'destructive'}>
                              {p.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default FinanceOverview;
